import { Badge } from '@/components/common/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent, TabsTrigger } from '@/components/common/tabs'
import { useConnectWalletModalContext } from '@/components/connect/connectModal'
import { TxSteps } from '@/components/opportunity/sidePanel/txSteps'
import { useTransaction } from '@/hooks/useTransaction'
import { type Action, useActionsQuery } from '@/queries/actions.query'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { formatDate, formatTokenValue, fromUnixTimestamp } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import type { Address } from 'viem'
import { z } from 'zod'
import { GenericNonInstantRedemptionNote } from './genericNonInstantRedemptionNote'

export const GENERIC_PENDING_TAB_NAME = 'genericPending'

const pendingRequestSchema = z.object({
  type: z.enum(['deposit', 'redeem']),
  status: z.enum(['pending', 'ready']),
  assetAmount: z.string(),
  timestamp: z.number().optional(),
  deadline: z.number().optional(),
})

const pendingRequestsSchema = z.array(pendingRequestSchema)

type PendingRequestData = z.infer<typeof pendingRequestSchema>

function getRawPendingRequests(transactionsContext: TransactionsContext): unknown {
  return (transactionsContext?.vaultSpecificData as Record<string, unknown> | undefined)?.pendingRequests
}

export function hasPendingRequestsField(transactionsContext: TransactionsContext): boolean {
  return Array.isArray(getRawPendingRequests(transactionsContext))
}

export function parsePendingRequests(transactionsContext: TransactionsContext): PendingRequestData[] {
  const raw = getRawPendingRequests(transactionsContext)
  if (!raw) return []
  const result = pendingRequestsSchema.safeParse(raw)
  return result.success ? result.data : []
}

interface GenericPendingTabProps {
  account: Address | undefined
  vault: Vault
  pendingRequests: PendingRequestData[]
  setTransactionFinished: ({ txHash, amount }: { txHash: string | undefined; amount: string }) => void
  txState: 'idle' | 'loading' | 'success'
}

export function GenericPendingTab({
  account,
  vault,
  pendingRequests,
  setTransactionFinished,
  txState,
}: GenericPendingTabProps) {
  const hasClaimableRedeem = pendingRequests.some((r) => r.type === 'redeem' && r.status === 'ready')
  const hasClaimableDeposit = pendingRequests.some((r) => r.type === 'deposit' && r.status === 'ready')
  const hasClaimable = hasClaimableRedeem || hasClaimableDeposit

  const claimActions: Action[] = []
  if (hasClaimableRedeem) claimActions.push('claim-redeem')
  if (hasClaimableDeposit) claimActions.push('claim-deposit')

  return (
    <TabsContent value={GENERIC_PENDING_TAB_NAME} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
      <PendingRequestsTable requests={pendingRequests} vault={vault} />

      {hasClaimable && (
        <ClaimSection
          account={account}
          vault={vault}
          claimActions={claimActions}
          label={'Claim'}
          setTransactionFinished={setTransactionFinished}
          txState={txState}
        />
      )}

      {!hasClaimable && (
        <div className="flex flex-col gap-2 text-center">
          <div className="text-gray-500 text-sm">
            Some actions on this vault may take time to process. Check back later for updates.
          </div>
        </div>
      )}

      <GenericNonInstantRedemptionNote protocol={vault.protocol} />
    </TabsContent>
  )
}

function ClaimSection({
  account,
  vault,
  claimActions,
  label,
  setTransactionFinished,
  txState,
}: {
  account: Address | undefined
  vault: Vault
  claimActions: Action[]
  label: string
  setTransactionFinished: GenericPendingTabProps['setTransactionFinished']
  txState: GenericPendingTabProps['txState']
}) {
  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()
  const { network } = vault

  const { data: redeemActions, isFetching: isFetchingRedeem } = useActionsQuery({
    address: account,
    action: 'claim-redeem',
    amount: '0',
    vault,
    all: true,
    ignoreAmount: true,
    enabled: claimActions.includes('claim-redeem'),
  })

  const { data: depositActions, isFetching: isFetchingDeposit } = useActionsQuery({
    address: account,
    action: 'claim-deposit',
    amount: '0',
    vault,
    all: true,
    ignoreAmount: true,
    enabled: claimActions.includes('claim-deposit'),
  })

  const combinedActions = useMemo(() => {
    const sources = [redeemActions, depositActions].filter(Boolean) as NonNullable<typeof redeemActions>[]
    if (sources.length === 0) return null
    const skipped = sources.flatMap((s) => s.actions.slice(0, s.currentActionIndex))
    const pending = sources.flatMap((s) => s.actions.slice(s.currentActionIndex))
    return { currentActionIndex: skipped.length, actions: [...skipped, ...pending] }
  }, [redeemActions, depositActions])

  const stepData: StepData = useMemo(() => {
    const actionNames = combinedActions?.actions.map((a) => a.name) ?? claimActions
    return { name: claimActions[0], actions: actionNames, actionsUrl: '' }
  }, [combinedActions, claimActions])

  const isFetching = isFetchingRedeem || isFetchingDeposit

  const { executeTx, currentActionIndex } = useTransaction(network.name, combinedActions, '0', setTransactionFinished)

  const onClaim = async () => {
    if (!account) {
      setOpenWalletModal('connect')
      return
    }
    await executeTx()
  }

  const isSubmitLoading = isFetching || txState === 'loading'

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onClaim()
      }}
      className="flex flex-col gap-3"
    >
      <TxSteps
        account={account}
        stepData={stepData}
        activeStep={currentActionIndex}
        loading={isSubmitLoading}
        label={label}
        network={network.name}
        success={txState === 'success'}
      />
    </form>
  )
}

export const GenericPendingTabTrigger = ({ className }: { className?: string }) => {
  return (
    <TabsTrigger value={GENERIC_PENDING_TAB_NAME} className={className}>
      Pending
    </TabsTrigger>
  )
}

function PendingRequestsTable({ requests, vault }: { requests: PendingRequestData[]; vault: Vault }) {
  const { asset } = vault
  const sortedRequests = [...requests].sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
  const hasTimestamps = requests.some((r) => r.timestamp)
  const hasDeadlines = requests.some((r) => r.deadline)

  if (requests.length === 0) {
    return <p className="text-center text-xs">No pending requests</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow variant="header">
          <TableHead>Action</TableHead>
          <TableHead>Amount</TableHead>
          {hasTimestamps && <TableHead>Date</TableHead>}
          {hasDeadlines && <TableHead>Deadline</TableHead>}
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedRequests.map((request, index) => (
          <TableRow key={`${request.type}-${index}`}>
            <TableCell>
              <p className="font-bold text-heading-foreground text-m capitalize">{request.type}</p>
            </TableCell>
            <TableCell>
              {formatTokenValue({ value: request.assetAmount, decimals: asset.decimals }, { minimumFractionDigits: 2 })}{' '}
              {asset.symbol}
            </TableCell>
            {hasTimestamps && (
              <TableCell>
                {request.timestamp
                  ? formatDate({
                      timestamp: fromUnixTimestamp(request.timestamp),
                    })
                  : '-'}
              </TableCell>
            )}
            {hasDeadlines && (
              <TableCell>
                {request.deadline
                  ? formatDate({
                      timestamp: fromUnixTimestamp(request.deadline),
                    })
                  : '-'}
              </TableCell>
            )}
            <TableCell>
              <Badge color={request.status === 'ready' ? 'green' : 'orange'}>
                {request.status === 'ready' ? 'Ready' : 'Pending'}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
